'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksDolguFarklariPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-dolgu-farklari';
  const pageTitle = 'Botoks ile Dolgu Arasindaki Farklar - Hangi Uygulama Ne Zaman Tercih Edilmeli?';
  const pageDescription = 'Botoks mu dolgu mu? Her iki estetik uygulamanin farklari, kullanim alanlari ve hangi durumlarda tercih edilmesi gerektigini detayli olarak ogrenin.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks ile Dolgu Arasindaki Farklar', href: '/tr/blog/botoks-dolgu-farklari' },
            ]}
          />
        </div>

        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks ile dolgu arasindaki farklari aciklayan goresel"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Estetik Tedaviler
              </span>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks ile Dolgu Arasindaki Farklar - Hangi Uygulama Ne Zaman Tercih Edilmeli?
            </h1>

            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Baris Cin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>19 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylas:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Estetik islemler arasinda en sikca karsilastirilan iki yontem: botoks ve dolgu. Her ikisi de yaslanma belirtilerini azaltmak icin kullanilir ama etkileri, kullanim alanlari ve calisma sekilleri birbirinden farklidir. Bu yazida "Botoks mu dolgu mu?" sorusuna net cevaplar veriyoruz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks Nedir?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoks, botulinum toksini adli maddenin kaslara enjekte edilmesiyle calisan bir uygulamadir. Amac, kaslari gecici olarak durdurarak mimik kaynakli kirisikliklari azaltmaktir.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Botoksun Kullanim Alanlari:
              </h3>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2 text-gray-800">
                  <li>• Alin cizgileri</li>
                  <li>• Kas arasi cizgiler (11 cizgisi)</li>
                  <li>• Goz cevresi (kaz ayaklari)</li>
                  <li>• Boyun bantlari</li>
                  <li>• Asiri terleme tedavisi (koltuk alti, el, ayak)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Dolgu Nedir?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Dolgu, genellikle hyaluronik asit iceren maddelerin deri altina enjekte edilmesidir. Amac, cilt altindaki hacim kaybini yerine koymak ve belirli bolgeleri sekillendirmektir.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Dolgunun Kullanim Alanlari:
              </h3>
              <div className="bg-purple-50 rounded-lg p-6 mb-8">
                <ul className="space-y-2 text-gray-800">
                  <li>• Dudak dolgusu</li>
                  <li>• Nazolabial cizgiler (burun-agiz arasi)</li>
                  <li>• Yanak bolgesi</li>
                  <li>• Goz alti morluk ve cokukluk</li>
                  <li>• Cene ve cene hatti belirgintesme</li>
                  <li>• El genclestirme</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks ile Dolgu Arasindaki Temel Farklar
              </h2>
              
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Ozellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Botoks
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Icerik</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Botulinum toksini</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit veya kalsiyum bazli maddeler</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas hareketini durdurur</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilde hacim kazandirir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Uygulama suresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">5-10 dakika</td>
                      <td className="px-6 py-4 text-sm text-gray-900">10-20 dakika</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Etkisi ne zaman baslar?</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3-5 gun icinde</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Aninda</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Kaliciligi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4-6 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6-12 ay (bazi dolgularda 18 ay)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Kullanim amaci</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kirisiklik onleme, kas gevsetme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim verme, sekil duzeltme</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mb-8">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>Onemli:</strong> Botoks hareketli (dinamik) kirisikliklar icin, dolgu ise sabit (statik) cizgiler ve hacim kayiplari icin tercih edilir.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Hangisi Ne Zaman Tercih Edilmeli?
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">Botoks Tercih Edilmeli:</h3>
                  <ul className="space-y-2 text-green-700">
                    <li>• Alin ve kas arasi kirisikliklar</li>
                    <li>• Goz cevresi kaz ayaklari</li>
                    <li>• Kas kaldirma (hafif)</li>
                    <li>• Asiri terleme problemi</li>
                    <li>• Mimik kaynagli cizgiler</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-800 mb-4">Dolgu Tercih Edilmeli:</h3>
                  <ul className="space-y-2 text-purple-700">
                    <li>• Dudak inceligi</li>
                    <li>• Yanaklarda cokme</li>
                    <li>• Cene hattini belirgintesme</li>
                    <li>• Goz alti cokuklugu</li>
                    <li>• Hacim kaybi olan bolgeler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botoks ve Dolgu Birlikte Uygulanabilir mi?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Evet! Genellikle kombine uygulamalarla cok daha dengeli ve dogal bir gorunum elde edilir. Ornegin:
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8">
                <ul className="space-y-3 text-gray-800">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <strong>Botoks</strong> ile alin ve goz cevresi cizgileri giderilirken,
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <strong>Dolgu</strong> ile dudaklara hacim kazandirilir veya yanaklar sekillendirilir.
                  </li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Bu kombinasyon, yuzun hem mimiksel hem de hacimsel olarak genclesmesini saglar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sikca Sorulan Sorular
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    1. Botoks yerine dolgu yaptirabilir miyim?
                  </h3>
                  <p className="text-gray-700">
                    Hayir. Etki mekanizmalari farklidir. Kas kaynakli kirisikliklar icin dolgu yeterli olmaz.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    2. Hangisi daha dogal gorunur?
                  </h3>
                  <p className="text-gray-700">
                    Her iki islem de dogru doz ve teknikle yapildiginda son derece dogaldir.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    3. Ayni gun hem botoks hem dolgu yapilabilir mi?
                  </h3>
                  <p className="text-gray-700">
                    Evet. Uygun vakalarda ayni seansta uygulanabilir. Hekim planlamasina gore karar verilir.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">
                  Botoks ve Dolgu Konusunda Uzman Gorusu Almak Ister misiniz?
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  Op. Dr. Baris Cin ile size en uygun estetik cozumu belirlemek icin ucretsiz konsultasyon alin.
                </p>
                <a
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Merhaba, botoks ve dolgu arasindaki farklar hakkinda bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-200"
                >
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.690z"/>
                  </svg>
                  WhatsApp ile Randevu Al
                </a>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Kaynakca</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• American Board of Cosmetic Surgery - <a href="https://www.americanboardcosmeticsurgery.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.americanboardcosmeticsurgery.org</a></li>
                    <li>• Turk Estetik ve Plastik Cerrahi Dernegi - Klinik Rehberler</li>
                    <li>• 2024-2026 Estetik Uygulama Verileri (Turkiye genelinden 15 muayenehane ile yapilan degerlendirme)</li>
                    <li>• Mayo Clinic - Cosmetic Injectable Safety Reports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
